// Opus in Ogg (RFC 7845), the format of every Voxie voice message: mono, 48 kHz, as the app's
// recorder writes it (app/.../audio/AudioImporter.kt, server/.../audio/OggOpus.kt). The browser's
// encoder (WebCodecs) gives bare Opus packets; this puts them into Ogg pages.

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let i = 0; i < 256; i++) {
    let r = i << 24;
    for (let j = 0; j < 8; j++) r = r & 0x80000000 ? (r << 1) ^ 0x04c11db7 : r << 1;
    table[i] = r >>> 0;
  }
  return table;
})();

/** Ogg's CRC-32 (polynomial 0x04c11db7, no reflection, initial 0). */
export function oggCrc(bytes: Uint8Array): number {
  let crc = 0;
  for (const b of bytes) crc = ((crc << 8) ^ CRC_TABLE[((crc >>> 24) ^ b) & 0xff]!) >>> 0;
  return crc;
}

const SAMPLE_RATE = 48_000;
/** Packets per page: 50 × 20 ms = one second, as libopus' tools do it roughly. */
const PACKETS_PER_PAGE = 50;

export class OggOpusWriter {
  private readonly pages: Uint8Array[] = [];
  private readonly serial: number;
  private sequence = 0;
  private pending: Uint8Array[] = [];
  /** Samples (at 48 kHz) of the packets written so far, without the pre-skip. */
  private samples = 0;
  private readonly preSkip: number;

  constructor(options: { preSkip?: number; serial?: number; vendor?: string } = {}) {
    this.preSkip = options.preSkip ?? 312;
    this.serial = options.serial ?? (Math.random() * 0xffffffff) >>> 0;
    this.page([opusHead(this.preSkip)], 0, 0x02);
    this.page([opusTags(options.vendor ?? "voxie-web")], 0, 0);
  }

  /** One Opus packet of [samples] samples at 48 kHz (960 for 20 ms). */
  add(packet: Uint8Array, samples: number) {
    this.pending.push(packet);
    this.samples += samples;
    if (this.pending.length >= PACKETS_PER_PAGE) this.flush(false);
  }

  /** The finished file; the last page carries the end-of-stream flag. */
  finish(): Uint8Array {
    this.flush(true);
    const size = this.pages.reduce((n, p) => n + p.length, 0);
    const out = new Uint8Array(size);
    let at = 0;
    for (const p of this.pages) {
      out.set(p, at);
      at += p.length;
    }
    return out;
  }

  get durationMs(): number {
    return Math.round((this.samples * 1000) / SAMPLE_RATE);
  }

  private flush(last: boolean) {
    if (this.pending.length === 0 && !last) return;
    this.page(this.pending, this.preSkip + this.samples, last ? 0x04 : 0);
    this.pending = [];
  }

  private page(packets: Uint8Array[], granule: number, flags: number) {
    const lacing: number[] = [];
    for (const p of packets) {
      let left = p.length;
      while (left >= 255) {
        lacing.push(255);
        left -= 255;
      }
      lacing.push(left);
    }
    const body = packets.reduce((n, p) => n + p.length, 0);
    const page = new Uint8Array(27 + lacing.length + body);
    const view = new DataView(page.buffer);
    page.set([0x4f, 0x67, 0x67, 0x53]); // "OggS"
    page[4] = 0;
    page[5] = flags;
    view.setUint32(6, granule % 0x100000000, true);
    view.setUint32(10, Math.floor(granule / 0x100000000), true);
    view.setUint32(14, this.serial, true);
    view.setUint32(18, this.sequence++, true);
    page[26] = lacing.length;
    page.set(lacing, 27);
    let at = 27 + lacing.length;
    for (const p of packets) {
      page.set(p, at);
      at += p.length;
    }
    view.setUint32(22, oggCrc(page), true);
    this.pages.push(page);
  }
}

function opusHead(preSkip: number): Uint8Array {
  const head = new Uint8Array(19);
  const view = new DataView(head.buffer);
  head.set(new TextEncoder().encode("OpusHead"));
  head[8] = 1; // version
  head[9] = 1; // channels
  view.setUint16(10, preSkip, true);
  view.setUint32(12, SAMPLE_RATE, true); // input sample rate
  view.setInt16(16, 0, true); // output gain
  head[18] = 0; // mapping family: mono/stereo
  return head;
}

function opusTags(vendor: string): Uint8Array {
  const v = new TextEncoder().encode(vendor);
  const tags = new Uint8Array(8 + 4 + v.length + 4);
  const view = new DataView(tags.buffer);
  tags.set(new TextEncoder().encode("OpusTags"));
  view.setUint32(8, v.length, true);
  tags.set(v, 12);
  view.setUint32(12 + v.length, 0, true);
  return tags;
}

/** The pre-skip in an OpusHead (WebCodecs' decoderConfig.description, if the browser gives one). */
export function preSkipOf(description: AllowSharedBufferSource | undefined): number | undefined {
  if (!description) return undefined;
  const bytes = ArrayBuffer.isView(description) ? new Uint8Array(description.buffer, description.byteOffset, description.byteLength) : new Uint8Array(description);
  if (bytes.length < 19 || new TextDecoder().decode(bytes.subarray(0, 8)) !== "OpusHead") return undefined;
  return bytes[10]! | (bytes[11]! << 8);
}
